/**
 * Pure helpers for per-profile UI configuration (lot P, plan §9.4–9.5):
 * ordering, experience-driven hiding and widget availability. Shared by the
 * sidebar, the dashboard and the profile editor.
 */
import {
  DASHBOARD_WIDGETS,
  NAV_SECTIONS,
  type ControlledResource,
  type DashboardWidgetId,
  type NavItemId,
  type NavSection,
  type ProfileExperience,
} from '../types/profiles';

/** In the Olisé experience use cases are hidden (D27); permissions are untouched. */
export const OLISE_HIDDEN_NAV_ITEMS: readonly NavItemId[] = ['usecases'];
export const OLISE_HIDDEN_DASHBOARD_WIDGETS: readonly DashboardWidgetId[] = [
  'timeline-velocity',
  'usecase-matrix',
  'charts',
];

/**
 * Widgets that depend on the Projects modules: unchecked by default in a new custom
 * dashboard and badged in the profile editor. The dashboard renders the ones it has
 * registered (my-projects, gallery-latest since L1; analysis-runs, review-requests
 * since L3; projects-kpis, portfolio-minimap since L4).
 */
export const PROJECT_DASHBOARD_WIDGETS: readonly DashboardWidgetId[] = [
  'projects-kpis',
  'my-projects',
  'gallery-latest',
  'analysis-runs',
  'portfolio-minimap',
  'review-requests',
];

export const NAV_SECTION_IDS = Object.keys(NAV_SECTIONS) as NavSection[];
export const DASHBOARD_WIDGET_IDS = Object.keys(DASHBOARD_WIDGETS) as DashboardWidgetId[];

export function navSectionItems(section: NavSection): NavItemId[] {
  return Object.keys(NAV_SECTIONS[section]) as NavItemId[];
}

export function navItemResource(section: NavSection, item: NavItemId): ControlledResource | undefined {
  return (NAV_SECTIONS[section] as Partial<Record<NavItemId, ControlledResource>>)[item];
}

export function isNavItemHiddenByExperience(item: NavItemId, experience: ProfileExperience): boolean {
  return experience === 'olise' && OLISE_HIDDEN_NAV_ITEMS.includes(item);
}

export function isWidgetHiddenByExperience(
  widget: DashboardWidgetId,
  experience: ProfileExperience
): boolean {
  return experience === 'olise' && OLISE_HIDDEN_DASHBOARD_WIDGETS.includes(widget);
}

/**
 * Listed ids first, in the given order, then the remaining items in their
 * original order. Unknown or duplicate ids in `order` are ignored.
 */
export function applyOrder<T>(
  items: readonly T[],
  getId: (item: T) => string,
  order?: readonly string[] | null
): T[] {
  if (!order || order.length === 0) return [...items];
  const byId = new Map(items.map((item) => [getId(item), item] as const));
  const listed: T[] = [];
  const seen = new Set<string>();
  for (const id of order) {
    const item = byId.get(id);
    if (item !== undefined && !seen.has(id)) {
      listed.push(item);
      seen.add(id);
    }
  }
  return [...listed, ...items.filter((item) => !seen.has(getId(item)))];
}

/** Returns a copy with the element at `index` moved by `delta` (no-op at the bounds). */
export function moveItem<T>(items: readonly T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return [...items];
  }
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
